import { Direction } from '@angular/cdk/bidi';
import { Overlay } from '@angular/cdk/overlay';
import { Component, Inject, OnInit, ViewContainerRef } from '@angular/core';
import { CardDesignComponent } from 'src/app/@shared/components/design/slide-up-card/card-design.component';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { SearchStationsResultMapComponent } from 'src/app/modules/route-planner/components/route-planner-results/results/search-station/stations-result-list/search-stations-result-map/search-stations-result-map.component';
import {
  MORE_ACTIONS_MENU,
  PRINT,
  PRINT_ARIA_LABEL,
  SHARE,
  ROUTE_PLANNING,
  SHOW_ON_MAP,
  STATIONS_LIST,
  ROUTE_DETAILS,
  STATION,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { SearchStopForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/search-stop-form';
import { StopFullData } from 'src/app/modules/route-planner/interfaces/route-planning/service/@schedule-service.interface';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { PrintService } from 'src/app/modules/route-planner/services/print/print.service';
import { MapService } from 'src/app/modules/route-planner/services/map/map.service';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';

@Component({
    selector: 'app-search-stations-results-container',
    templateUrl: './search-stations-results-container.component.html',
    styleUrl: './search-stations-results-container.component.scss',
    standalone: false
})
export class SearchStationsResultsContainerComponent
  extends CardDesignComponent
  implements OnInit
{
  isFavoriteLine: boolean = false;
  direction: Direction;

  shareText: string;
  routePlanningText: string;
  stationsListText: string;
  showOnMapText: string;
  moreActionsMenuText: string;
  viewContainerRef: ViewContainerRef;

  moreActionsMenuItems: SelectOption<string>[] = [
    {
      label: 'הדפס',
      value: 'print',
      icon: 'print',
      onClick: () => this.handlePrint(),
    },
  ];

  constructor(
    routePlanningService: RoutePlanningService,
    resizeService: ResizeService,
    overlay: Overlay,

    @Inject(DATA_INJECTION_TOKEN)
    public readonly data: {
      formValues: SearchStopForm;
      stations: StopFullData[];
      mapService: MapService;
    },

    private readonly modalService: ModalService,
    private readonly languageService: LanguageService,
    private readonly printService: PrintService,
    private readonly mapService: MapService,
    private readonly queryParamsService: QueryParamsService
  ) {
    super(routePlanningService, resizeService, overlay);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.routePlanningService.setMapService(this.mapService);
    this.openResultsMap();
    this.initializeMultiLanguageTexts();
  }

  openResultsMap() {
    if (!this.isMobile) {
      this.routePlanningService._mapOverlaySub.next(null)
      setTimeout(() => {

        this.routePlanningService._mapOverlaySub.next({
          mapComponent: SearchStationsResultMapComponent,
          data: this.data,
        });
      }, 100)
    }
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction;
      this.shareText = SHARE[language.language];
      this.routePlanningText = ROUTE_PLANNING[language.language];
      this.stationsListText = STATIONS_LIST[language.language];
      this.showOnMapText = SHOW_ON_MAP[language.language];
      this.moreActionsMenuItems[0].label = PRINT[language.language];
      this.moreActionsMenuItems[0].ariaLabel = PRINT_ARIA_LABEL[language.language];
      this.moreActionsMenuText = MORE_ACTIONS_MENU[language.language];
    });
  }

  openShareLinkPopup() {
    if (this.data && this.data.formValues && this.data.formValues.poi) {
      console.debug(
        'Preserving station information for sharing:',
        this.data.formValues.poi
      );

      const params: any = {
        poi: this.data.formValues.poi.name,
        poiId: this.data.formValues.poi.id,
        tab: eTab.STATIONS,
        saveParams: true,
      };

      if (this.data.formValues.poi.lat && this.data.formValues.poi.lng) {
        params.poiLat = this.data.formValues.poi.lat;
        params.poiLng = this.data.formValues.poi.lng;
      }

      // Preserve poiSelectedIndex if it exists in current params
      const currentIndex =
        this.queryParamsService.currentParams['poiSelectedIndex'];
      if (currentIndex !== undefined) {
        params.poiSelectedIndex = currentIndex;
      }

      this.queryParamsService.setQueryParams(params);
    }

    setTimeout(() => {
      const currentUrl = window.location.href;
      navigator.clipboard
        .writeText(currentUrl)
        .then(() => {
          console.debug('URL copied to clipboard:', currentUrl);
        })
        .catch((err) => {
          console.error('Could not copy URL to clipboard:', err);
        });

      this.modalService.openShareLinkModal();
    }, 300);
  }

  handlePrint() {
    // Show loading indicator immediately - use setTimeout to allow browser to render
    this.printService.showLoadingIndicator();
    
    // Use setTimeout(0) to yield to browser rendering before continuing
    setTimeout(() => {
      const printableElement = document.getElementById('printableArea');
      if (!printableElement) {
        console.warn('No printable content found!');
        return;
      }
      const mapTab = document.querySelector(
        '[mat-tab-label*="map" i], [mat-tab-label*="מפה" i]'
      );
      if (mapTab) {
        (mapTab as HTMLElement).click();
        setTimeout(() => this.printContent(printableElement), 10);
      } else {
        this.printContent(printableElement);
      }
    }, 0);
  }

  private printContent(printableElement: HTMLElement) {
    const currentLanguage = this.languageService.getSelectedLanguage().language;

    const headerInfo = {
      title: ROUTE_DETAILS[currentLanguage],
      details: [
        {
          label: STATION[currentLanguage],
          value: this.data.formValues.poi.name || 'N/A',
        },
      ],
    };

    const mapService = this.routePlanningService.getMapService();
    this.printService.captureAndPrint(
      printableElement,
      headerInfo,
      mapService,
      2,
      null,
      14
    );
  }

  override handleOverlayEscape(): void {
    this.routePlanningService._mapOverlaySub.next(null);
    this.closeCard();
  }
}
